import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Briefcase, Plus, Users } from 'lucide-react'
import AppShell from '../../components/b2b/AppShell'
import DemoBadge from '../../components/b2b/DemoBadge'
import { api, APIError } from '../../api/client'

export default function JobsListScreen() {
  const [jobs, setJobs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    api.get('/api/b2b/jobs')
      .then((data) => { if (!cancelled) setJobs(data) })
      .catch((err) => {
        if (cancelled) return
        if (err instanceof APIError && err.status >= 500) {
          setError('Could not load jobs. Try refreshing.')
        } else if (!(err instanceof APIError)) {
          setError('Network error.')
        }
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  return (
    <AppShell>
      <div className="px-6 md:px-10 py-8 md:py-12 max-w-5xl mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-ink-1">Jobs</h1>
            <p className="text-sm text-ink-3 mt-1">All open jobs in your workspace.</p>
          </div>
          <Link
            to="/b2b/jobs/new"
            className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-5 min-h-[44px] rounded-2xl transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
          >
            <Plus className="w-4 h-4" />
            New job
          </Link>
        </div>

        {error && (
          <div className="mt-6 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
            {error}
          </div>
        )}

        <div className="mt-8">
          {loading ? (
            <p className="text-sm text-ink-3">Loading…</p>
          ) : jobs.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {jobs.map((job) => <JobCard key={job.id} job={job} />)}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  )
}

function EmptyState() {
  return (
    <div className="bg-white rounded-3xl shadow-card p-12 text-center">
      <div className="w-12 h-12 rounded-2xl bg-accent/10 text-accent flex items-center justify-center mx-auto">
        <Briefcase className="w-5 h-5" />
      </div>
      <h2 className="mt-4 text-lg font-bold text-ink-1">No jobs yet</h2>
      <p className="mt-1 text-sm text-ink-3 max-w-sm mx-auto">
        Create your first job brief to start screening candidates.
      </p>
      <Link
        to="/b2b/jobs/new"
        className="mt-6 inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-5 py-2.5 rounded-2xl transition"
      >
        <Plus className="w-4 h-4" />
        New job
      </Link>
    </div>
  )
}

function JobCard({ job }) {
  return (
    <Link
      to={`/b2b/jobs/${job.id}`}
      className="bg-white rounded-2xl shadow-card hover:shadow-card-hover p-5 flex items-center gap-4 transition"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="text-base font-bold text-ink-1 truncate">{job.title}</h2>
          <StatusPill status={job.status} />
          {job.is_demo && <DemoBadge />}
        </div>
        <p className="text-xs text-ink-3 mt-1">
          {[job.company_name, job.department, job.location].filter(Boolean).join(' • ') || 'No details yet'}
        </p>
      </div>
      <div className="flex items-center gap-2 text-ink-3 text-sm">
        <Users className="w-4 h-4" />
        <span>{job.candidate_count}</span>
      </div>
    </Link>
  )
}

function StatusPill({ status }) {
  const styles = {
    draft: 'bg-gray-100 text-gray-600',
    active: 'bg-green-100 text-green-700',
    archived: 'bg-amber-100 text-amber-700',
  }
  return (
    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${styles[status] || styles.draft}`}>
      {status}
    </span>
  )
}
